import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

const V2CommonContext = createContext(null);

/** Viewport below `md` (default 900px) — same as MUI `theme.breakpoints.down('md')`. */
export const V2_MOBILE_MAX = 'md';

export function useV2Common() {
  const ctx = useContext(V2CommonContext);
  if (!ctx) {
    throw new Error('useV2Common must be used within V2CommonProvider');
  }
  return ctx;
}

/**
 * V2 app-wide UI state (errors, modals) and viewport flags (`isMobile` / `isDesktop`).
 * Must render inside MUI `ThemeProvider` (see `V2App`) so breakpoint queries work.
 */
export function V2CommonProvider({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(V2_MOBILE_MAX));
  const isDesktop = !isMobile;

  const [globalError, setGlobalError] = useState(null);

  const showError = useCallback((payload) => {
    setGlobalError(payload ?? { message: 'Something went wrong' });
  }, []);

  const clearError = useCallback(() => setGlobalError(null), []);

  const value = useMemo(
    () => ({
      globalError,
      showError,
      clearError,
      isMobile,
      isDesktop,
    }),
    [globalError, showError, clearError, isMobile, isDesktop]
  );

  return <V2CommonContext.Provider value={value}>{children}</V2CommonContext.Provider>;
}
